import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import { fetchOwnBookById } from "../../../controller/addBook/premium/ownBooksController";

const READER_STATE_KEY = "own-book-reader-state";
const LAST_OPENED_BOOK_KEY = "own-book-last-opened";

const Wrapper = styled.section`
  width: 100%;
  padding: 2rem;
  background: var(--white);

  .top-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;
  }

  .back-btn {
    border: 1px solid var(--grey-300);
    background: #fff;
    color: var(--grey-700);
    border-radius: var(--borderRadius);
    padding: 0.45rem 0.85rem;
    cursor: pointer;
  }

  .book-image {
    width: min(320px, 100%);
    border-radius: 10px;
    box-shadow: var(--shadow-2);
    margin-bottom: 1.3rem;
  }

  .book-image-wrap {
    display: flex;
    justify-content: center;
    margin-bottom: 1.25rem;
  }

  .reader-controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.9rem;
  }

  .reader-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .page-size-switch {
    display: inline-flex;
    border: 1px solid var(--grey-300);
    border-radius: 999px;
    overflow: hidden;
    background: #fff;
  }

  .page-size-btn {
    border: none;
    background: transparent;
    padding: 0.42rem 0.85rem;
    cursor: pointer;
    color: var(--grey-700);
    font-weight: 600;
  }

  .page-size-btn.active {
    background: var(--primary-500);
    color: #fff;
  }

  .page-index {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--grey-700);
  }

  .bookmark-chip {
    border: 1px solid #d8b36b;
    background: #f8e9c8;
    color: #65481f;
    border-radius: 999px;
    padding: 0.22rem 0.6rem;
    font-size: 0.78rem;
    font-weight: 700;
  }

  .bookmark-btn {
    border: 1px solid #d8b36b;
    border-radius: 999px;
    background: #fff;
    color: #7a5a26;
    padding: 0.34rem 0.72rem;
    font-size: 0.8rem;
    font-weight: 700;
    cursor: pointer;
  }

  .book-page {
    position: relative;
    padding: 1.4rem 1.2rem;
    border-radius: 14px;
    border: 1px solid #d7c8b6;
    background: linear-gradient(180deg, #fbf7ef 0%, #f7f0e3 100%);
    box-shadow: 0 10px 24px rgba(24, 17, 10, 0.12), inset 0 0 0 1px rgba(255, 255, 255, 0.45);
  }

  .book-page::before {
    content: "";
    position: absolute;
    left: 0.9rem;
    top: 0.9rem;
    bottom: 0.9rem;
    width: 3px;
    background: rgba(98, 69, 40, 0.24);
    border-radius: 999px;
  }

  .page-paragraphs {
    display: flex;
    flex-direction: column;
    gap: 0.95rem;
    padding-left: 0.9rem;
  }

  .paragraph {
    margin: 0;
    line-height: 1.8;
    color: #2a2218;
    font-size: 1rem;
  }

  .pager {
    margin-top: 0.9rem;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.55rem;
  }

  .pager-btn {
    border: 1px solid var(--grey-300);
    border-radius: var(--borderRadius);
    background: #fff;
    color: var(--grey-700);
    padding: 0.42rem 0.8rem;
    cursor: pointer;
    font-weight: 600;
  }

  .pager-btn:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  @media (max-width: 768px) {
    padding: 1rem;

    .book-page {
      padding: 1rem 0.85rem;
    }

    .paragraph {
      font-size: 0.96rem;
      line-height: 1.72;
    }
  }

  .empty {
    min-height: 55vh;
    display: grid;
    place-items: center;
    text-align: center;
  }

  .cta-btn {
    border: none;
    background: var(--primary-500);
    color: #fff;
    border-radius: var(--borderRadius);
    padding: 0.75rem 1.6rem;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
  }
`;

const getOwnBookId = (entry) => entry?._id || entry?.id || entry?.bookId;
const getBookPayload = (entry) => entry?.book || entry || {};

const toParagraphArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return { paragraph: item.trim() };
        if (item && typeof item.paragraph === "string") return { paragraph: item.paragraph.trim() };
        return null;
      })
      .filter((item) => item && item.paragraph);
  }

  if (typeof value === "string" && value.trim()) {
    return [{ paragraph: value.trim() }];
  }

  return [];
};

const extractParagraphs = (entry, book) => {
  const candidates = [
    book?.paragraphs,
    entry?.paragraphs,
    book?.book?.paragraphs,
    entry?.book?.paragraphs,
    book?.paragraphText,
    entry?.paragraphText,
    book?.text,
    entry?.text,
  ];

  for (const candidate of candidates) {
    const normalized = toParagraphArray(candidate);
    if (normalized.length > 0) return normalized;
  }

  return [];
};

const getReaderStateMap = () => {
  try {
    const raw = localStorage.getItem(READER_STATE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
};

const saveReaderState = (bookId, nextState) => {
  if (!bookId) return;
  const stateMap = getReaderStateMap();
  stateMap[bookId] = {
    ...(stateMap[bookId] || {}),
    ...nextState,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(READER_STATE_KEY, JSON.stringify(stateMap));
};

const getBookReaderState = (bookId) => {
  if (!bookId) return null;
  const stateMap = getReaderStateMap();
  return stateMap[bookId] || null;
};

const OwnBookDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { bookId } = useParams();
  const [paragraphsPerPage, setParagraphsPerPage] = useState(2);
  const [currentPage, setCurrentPage] = useState(1);
  const [direction, setDirection] = useState(1);
  const [bookmarkPage, setBookmarkPage] = useState(null);
  const { selectedOwnBook, isLoading, error } = useSelector((store) => store.ownBooks);

  useEffect(() => {
    const saved = getBookReaderState(bookId);
    if (saved?.paragraphsPerPage === 1 || saved?.paragraphsPerPage === 2) {
      setParagraphsPerPage(saved.paragraphsPerPage);
    } else {
      setParagraphsPerPage(2);
    }
    setCurrentPage(saved?.currentPage && saved.currentPage > 0 ? saved.currentPage : 1);
    setBookmarkPage(saved?.bookmarkPage && saved.bookmarkPage > 0 ? saved.bookmarkPage : null);
  }, [bookId]);

  useEffect(() => {
    if (bookId) {
      dispatch(fetchOwnBookById(bookId));
    }
  }, [dispatch, bookId]);

  const selectedEntry = useMemo(() => {
    if (selectedOwnBook) return selectedOwnBook;
    const fromState = state?.book;
    if (fromState && String(getOwnBookId(fromState)) === String(bookId)) {
      return fromState;
    }
    return null;
  }, [state, selectedOwnBook, bookId]);

  const selectedBook = getBookPayload(selectedEntry);
  const image = selectedBook?.imageUrl || selectedBook?.image || selectedBook?.formats?.["image/jpeg"];
  const paragraphs = extractParagraphs(selectedEntry, selectedBook);
  const totalPages = Math.max(1, Math.ceil(paragraphs.length / paragraphsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!bookId) return;
    saveReaderState(bookId, {
      currentPage,
      paragraphsPerPage,
      bookmarkPage,
      maxReadPage: Math.max(currentPage, getBookReaderState(bookId)?.maxReadPage || 1),
    });
    localStorage.setItem(LAST_OPENED_BOOK_KEY, String(bookId));
  }, [bookId, currentPage, paragraphsPerPage, bookmarkPage]);

  const pageStart = (currentPage - 1) * paragraphsPerPage;
  const currentParagraphs = paragraphs.slice(pageStart, pageStart + paragraphsPerPage);

  const pageVariants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 90 : -90, rotateY: dir > 0 ? -7 : 7 }),
    center: { opacity: 1, x: 0, rotateY: 0 },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -90 : 90, rotateY: dir > 0 ? 7 : -7 }),
  };

  const onPrevPage = () => {
    setDirection(-1);
    setCurrentPage((page) => Math.max(1, page - 1));
  };

  const onNextPage = () => {
    setDirection(1);
    setCurrentPage((page) => Math.min(totalPages, page + 1));
  };

  const onToggleBookmark = () => {
    setBookmarkPage((prev) => (prev === currentPage ? null : currentPage));
  };

  const onJumpBookmark = () => {
    if (!bookmarkPage || bookmarkPage > totalPages) return;
    setDirection(bookmarkPage > currentPage ? 1 : -1);
    setCurrentPage(bookmarkPage);
  };

  if (!isLoading && !selectedEntry) {
    return (
      <Wrapper>
        <div className="empty">
          <div>
            <h3>No book found</h3>
            <button className="cta-btn" onClick={() => navigate("/")}>Add New Book</button>
          </div>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="top-row">
        <button className="back-btn" onClick={() => navigate("/own-books")}>Back to Books</button>
      </div>

      {isLoading && <p>Loading book...</p>}
      {!isLoading && error && <p className="paragraph">Failed to load book details. {error}</p>}

      {!isLoading && !error && selectedEntry && (
        <>
          {image && (
            <div className="book-image-wrap">
              <img className="book-image" src={image} alt="Book cover" />
            </div>
          )}

          <div className="reader-controls">
            <div className="page-size-switch">
              <button
                type="button"
                className={`page-size-btn ${paragraphsPerPage === 1 ? "active" : ""}`}
                onClick={() => setParagraphsPerPage(1)}
              >
                1 / page
              </button>
              <button
                type="button"
                className={`page-size-btn ${paragraphsPerPage === 2 ? "active" : ""}`}
                onClick={() => setParagraphsPerPage(2)}
              >
                2 / page
              </button>
            </div>

            <div className="reader-meta">
              <div className="page-index">Page {currentPage} of {totalPages}</div>
              {bookmarkPage && bookmarkPage <= totalPages && (
                <button type="button" className="bookmark-chip" onClick={onJumpBookmark}>
                  Bookmark: Page {bookmarkPage}
                </button>
              )}
              <button type="button" className="bookmark-btn" onClick={onToggleBookmark}>
                {bookmarkPage === currentPage ? "Remove Bookmark" : "Bookmark This Page"}
              </button>
            </div>
          </div>

          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={`${bookId}-${currentPage}-${paragraphsPerPage}`}
              className="book-page"
              custom={direction}
              variants={pageVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.33, ease: "easeInOut" }}
            >
              <div className="page-paragraphs">
                {paragraphs.length > 0 ? (
                  currentParagraphs.map((item, index) => (
                    <p className="paragraph" key={`${pageStart + index}-${item?.paragraph?.slice(0, 12) || "p"}`}>
                      {item?.paragraph}
                    </p>
                  ))
                ) : (
                  <p className="paragraph">No paragraphs available for this book yet.</p>
                )}
              </div>
            </motion.div>
          </AnimatePresence>

          {paragraphs.length > 0 && (
            <div className="pager">
              <button
                type="button"
                className="pager-btn"
                disabled={currentPage <= 1}
                onClick={onPrevPage}
              >
                Previous
              </button>
              <button
                type="button"
                className="pager-btn"
                disabled={currentPage >= totalPages}
                onClick={onNextPage}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default OwnBookDetails;
